import { useGetProfile } from '@sunrise-ui/api-client';
import type { Profile } from '@sunrise-ui/api/profile/api';
import {
  ProfileBasicInfo,
  ProfileImg,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React, { type PropsWithChildren } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native';

type Props = {
  userId?: string;
  shouldNavigateOnClick?: boolean;
  size?: 'small' | 'medium';
  showFollowButton?: boolean;
  style?: ViewStyle;
  profile?: Profile; // Pre-fetched profile data to avoid individual API calls
  isOneLineText?: boolean;
};

export function ProfileResult({
  userId,
  shouldNavigateOnClick,
  children,
  showFollowButton,
  size = 'medium',
  style,
  profile: preFetchedProfile,
  isOneLineText,
}: PropsWithChildren<Props>) {
  const { currentUserId } = useAuthStore();
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);

  // Only fetch profile if no pre-fetched profile is provided
  const { data: apiProfile, isLoading: isLoadingProfile } = useGetProfile(
    preFetchedProfile ? undefined : userId,
  );

  // Use pre-fetched profile if available, otherwise use API profile
  const profile = preFetchedProfile || apiProfile;
  const isLoading = !preFetchedProfile && isLoadingProfile;

  const isSelf = currentUserId === userId;

  // Navigate to user's profile using expo-router
  const navigateToProfile = () => {
    if (userId) {
      router.navigate(`/to/${userId}`);
    }
  };

  // Following functionality would be handled by a FollowButton component
  // We'll leave a placeholder here for now
  const followButton =
    showFollowButton && !isSelf && userId ? (
      <View style={styles.followButton}>
        {/* <FollowButton userId={userId} /> */}
      </View>
    ) : null;

  const content = (
    <>
      <ProfileImg
        userId={userId}
        imgSize={size === 'small' ? 'thumbnail_large' : 'large'}
        styleSize={isMobile || size === 'small' ? 50 : 95}
        profile={profile}
        isLoading={isLoading}
      />
      <ProfileBasicInfo
        firstName={profile?.first_name}
        lastName={profile?.last_name}
        pronouns={profile?.pronouns}
        headline={profile?.headline}
        isLoading={isLoading}
        size={size}
        nameButton={followButton}
        isOneLineText={isOneLineText}
      />
      {children}
    </>
  );

  if (shouldNavigateOnClick)
    return (
      <TouchableOpacity
        style={[styles.container, style]}
        onPress={navigateToProfile}
      >
        {content}
      </TouchableOpacity>
    );

  return <View style={[styles.container, style]}>{content}</View>;
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      display: 'flex',
      flexDirection: 'row',
      gap: 8,
      alignItems: 'center',
      flexGrow: 1,
      flexShrink: 1,
    },
    textContainer: {
      display: 'flex',
    },
    followButton: {
      marginLeft: 8,
    },
  });
